import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Lightweight Workspace shape used on the frontend.
 *
 * This mirrors the API response from GET /api/v1/workspaces.
 * We define it locally here rather than importing from @gradnomad/types because
 * the types package uses `const enum` (erased at compile time) and the frontend
 * is a separate npm project with its own tsc compilation boundary.
 */
export interface Workspace {
  id: string
  name: string
  type: 'INDIVIDUAL' | 'ADVISOR'
  /** Subscription tier for badge display in the sidebar */
  subscriptionTier: 'FREE' | 'PRO' | 'MAX'
  /** The current user's role in this workspace */
  role: 'OWNER' | 'STUDENT' | 'VIEWER'
  createdAt: string
  updatedAt: string
}

export interface WorkspaceStore {
  currentWorkspaceId: string | null
  currentWorkspace: Workspace | null
  workspaces: Workspace[]

  /** Currently selected application cycle ID */
  selectedCycleId: string | null

  /**
   * Sets the active workspace by ID. Updates both currentWorkspaceId (persisted)
   * and currentWorkspace (derived from the workspaces array).
   */
  switchWorkspace: (id: string) => void

  /**
   * Replaces the full workspace list (called after a successful API fetch).
   * Also updates currentWorkspace if it has changed server-side.
   */
  setWorkspaces: (workspaces: Workspace[]) => void

  /** Sets the selected cycle for the applications kanban view. */
  setSelectedCycleId: (id: string | null) => void

  /** Clears all persisted state — call on sign-out and account deletion. */
  reset: () => void
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Workspace Zustand store.
 *
 * Persistence strategy:
 * - Only `currentWorkspaceId` and `selectedCycleId` are written to localStorage
 *   (key: `gradnomad_workspace`).
 * - Full workspace objects are NOT persisted; they are refreshed from the API
 *   on every app load via the `useWorkspace` hook.
 * - This avoids stale data (e.g., a renamed workspace) surviving across sessions.
 *
 * The localStorage key `gradnomad_workspace` is also read directly by api.ts
 * to extract the active workspace ID for the X-Workspace-Id request header.
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      currentWorkspaceId: null,
      currentWorkspace: null,
      workspaces: [],
      selectedCycleId: null,

      switchWorkspace: (id: string) => {
        const workspace = get().workspaces.find((w) => w.id === id) ?? null
        set({ currentWorkspaceId: id, currentWorkspace: workspace })
      },

      setWorkspaces: (workspaces: Workspace[]) => {
        const currentId = get().currentWorkspaceId
        const found = workspaces.find((w) => w.id === currentId)
        const currentWorkspace = found ?? workspaces[0] ?? null

        set({
          workspaces,
          currentWorkspace,
          currentWorkspaceId: currentWorkspace?.id ?? null,
          // If the persisted workspace ID wasn't in the returned list, the user
          // switched accounts — stale cycle IDs from the old account must be cleared
          selectedCycleId: found ? get().selectedCycleId : null,
        })
      },

      setSelectedCycleId: (id: string | null) => {
        set({ selectedCycleId: id })
      },

      reset: () => {
        set({
          currentWorkspaceId: null,
          currentWorkspace: null,
          workspaces: [],
          selectedCycleId: null,
        })
      },
    }),
    {
      name: 'gradnomad_workspace',
      storage: createJSONStorage(() => localStorage),
      // Only persist the IDs — workspace objects are re-hydrated from the API
      partialize: (state) => ({
        currentWorkspaceId: state.currentWorkspaceId,
        selectedCycleId: state.selectedCycleId,
      }),
    },
  ),
)
